import React, { FC, memo, useContext } from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfFooter from '../PdfComponents/PdfFooter';
import PdfHeaderDetails from '../PdfComponents/PdfHeaderDetails';
import PdfSupplierDetails from '../PdfComponents/PdfSupplierDetails';
import PdfMainDetails from '../PdfComponents/PdfMainDetails';
import PdfTitleTextBox from '../PdfComponents/PdfTitleTextBox';
import { PdfRemarksSection } from '../PdfComponents/PdfSection';
import PdfPassengerDetails from '../PdfComponents/PdfPassengerDetails';
import PdfTableComponent from '../PdfComponents/PdfTable';
import PdfBillingContactDetails from '../PdfComponents/PdfBillingContactDetails';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import { VouchersPdfPropsType } from './voucherHelpers';

const PRQPdf: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const clientsContext = useContext(ClientsContext);

    const {
        info: {
            dueDate,
            travelFileNumber,
            travelFileOpenDate,
            travelFileStartDate,
            remark,
            coveredBy,
            supplierName,
            pdfInfo: {
                userName,
                showIssuedBy,
                voucherCertification = 'Draft',
                pdfImageSource = (clientsContext.selected as Client)?.companyLogoUrl,
                bottomDetails,
                formOfPayment,
                linkedTo,
                topLeftDetailsText,
                passenger,
                agent,
                supplierDetails,
                columns,
                rows,
                summaryLine,
                rateLine,
                companyInfo,
                columnsVouchers,
                rowsVouchers,
                summaryLineVouchers,
                approval
            }
        },
        number: voucherNumber,
        type: voucherType,
        createdAt
    } = props;

    return (
        <Document title={`${voucherType} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={styles.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    <View style={styles.section}>
                        <View style={{ ...styles.header, ...styles.spaceBetween }}>
                            <View>
                                {passenger ? <PdfPassengerDetails passenger={passenger} /> : <></>}
                                {agent ? <PdfBillingContactDetails {...agent} /> : <></>}
                                {supplierDetails ? (
                                    <PdfSupplierDetails
                                        supplierName={supplierName}
                                        supplierDetails={supplierDetails}
                                    />
                                ) : !passenger && topLeftDetailsText ? (
                                    <Text>{topLeftDetailsText}</Text>
                                ) : <></>}
                            </View>
                            <PdfHeaderDetails
                                iata={companyInfo.iata}
                                issueDate={props.issueDate || createdAt}
                                dueDate={dueDate}
                                travelFileNumber={travelFileNumber}
                                travelFileOpenDate={travelFileOpenDate}
                                travelFileStartDate={travelFileStartDate}
                            />
                        </View>
                    </View>
                    <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                        <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                        {coveredBy?.type && coveredBy?.number ? (
                            <Text style={{ ...styles.littleText }}>
                                {coveredBy.type} No. {coveredBy.number}
                            </Text>
                        ) : (
                            <></>
                        )}
                    </View>

                    <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                        <PdfMainDetails
                            topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                            bottomDetails={bottomDetails}
                            withForLabel
                        />
                    </View>

                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent
                            columns={columns}
                            rows={rows}
                            summaryLine={summaryLine}
                            voucherNumber={voucherNumber}
                            travelFileNumber={travelFileNumber}
                        />
                    </View>

                    {rateLine ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, styles.fontBold]}>{rateLine}</Text>
                        </View>
                    ) : <></>}

                    <PdfRemarksSection body={formOfPayment} title="Form of Payment" />
                    <PdfRemarksSection body={linkedTo} title="Linked to" />
                    <PdfRemarksSection body={remark} />

                    <View style={{ marginTop: 48, marginHorizontal: 10, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                        <View style={{ flex: 1 }}>
                            <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#999', width: '80%', marginBottom: 4, paddingBottom: 4 }}>
                                <Text style={{ fontSize: BASE_FONT_SIZE, color: '#777' }}>
                                    Name: {approval?.approverName || ''}
                                </Text>
                            </View>
                        </View>
                        <View style={{ flex: 1 }}>
                            <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#999', width: '80%', marginBottom: 4, paddingBottom: 4 }}>
                                <Text style={{ fontSize: BASE_FONT_SIZE, color: '#777' }}>
                                    Date: {approval?.approvalDate ? new Date(approval.approvalDate).toLocaleDateString() : ''}
                                </Text>
                            </View>
                        </View>
                        <View style={{ flex: 1 }}>
                            {approval?.signatureData ? (
                                <View style={{ width: '80%', marginBottom: 4 }}>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, color: '#777', marginBottom: 2 }}>Signature:</Text>
                                    <Image source={approval.signatureData} style={{ width: 120, height: 50 }} />
                                </View>
                            ) : (
                                <View style={{ borderBottomWidth: 0.5, borderBottomColor: '#999', width: '80%', marginBottom: 4, paddingBottom: 4 }}>
                                    <Text style={{ fontSize: BASE_FONT_SIZE, color: '#777' }}>Signature:</Text>
                                </View>
                            )}
                        </View>
                    </View>

                    {showIssuedBy !== false && !!userName && <Text style={{ fontSize: BASE_FONT_SIZE - 1, color: '#6b7280', marginTop: 6, marginHorizontal: 10 }}>Issued by: {userName}</Text>}
                </View>

                <PdfFooter companyInfo={companyInfo} />
            </Page>
            {/* Second Page: */}
            {columnsVouchers && rowsVouchers && (
                <Page wrap size="A4" style={styles.page}>
                    <PdfLogoComponent pdfImageSource={pdfImageSource} />

                    <View style={styles.padding0_10}>
                        <View style={styles.section}>
                            <View style={{ ...styles.header, ...styles.spaceBetween }}>
                                <View>
                                    {passenger ? (
                                        <PdfPassengerDetails passenger={passenger} />
                                    ) : (
                                        <></>
                                    )}
                                    {agent ? <PdfBillingContactDetails {...agent} /> : <></>}
                                    {supplierDetails ? (
                                        <PdfSupplierDetails
                                            supplierName={supplierName}
                                            supplierDetails={supplierDetails}
                                        />
                                    ) : !passenger && topLeftDetailsText ? (
                                        <Text>{topLeftDetailsText}</Text>
                                    ) : <></>}
                                </View>
                                <PdfHeaderDetails
                                    iata={companyInfo.iata}
                                    travelFileNumber={travelFileNumber}
                                    issueDate={props.issueDate || createdAt}
                                    dueDate={dueDate}
                                />
                            </View>
                        </View>
                        <View style={[styles.alignItemsFlexEnd, styles.margin2_10]}>
                            <Text style={{ ...styles.button }}>{voucherCertification}</Text>
                        </View>

                        <View style={{ ...styles.section, ...styles.alignCenter }} wrap={false}>
                            <PdfMainDetails
                                topDetails={`${voucherType} No. ${voucherNumber || 'No Document Number'}`}
                                bottomDetails={bottomDetails}
                                withForLabel
                            />
                        </View>

                        <View style={[styles.section]}>
                            <Text>This payment applies to the following vouchers/tickets: </Text>
                            <PdfTableComponent
                                columns={columnsVouchers}
                                rows={rowsVouchers}
                                summaryLine={summaryLineVouchers}
                            />
                            <Text style={[styles.littleText, styles.paddingTop10]}>
                                Commission and VAT calculations are deducted once against the tax invoice
                                document
                            </Text>
                            <Text style={[styles.littleText]}>
                                S: Supplier invoice T: Ticket num P: Reservation #/ PNR
                            </Text>
                        </View>
                    </View>

                    <PdfFooter companyInfo={companyInfo} />
                </Page>
            )}
        </Document>
    );
};

export default memo(PRQPdf);
